import React, { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Category } from '../types/foundObject';
import { DASHBOARD_BRAND_PRIMARY } from '../theme/dashboardBrand';
import FoundObjectSecretDetailButton from './FoundObjectSecretDetailButton';
import {
  categoryAttributesRecord,
  collectSecretAttributeEntries,
  formatAttributeValueForCard,
  orderedCardAttributeKeys,
  resolveObjectAttributeValue,
} from '../utils/objectAttributeDisplay';
import { resolveCategoryAttributeDisplayLabel } from '../utils/categoryAttributeFilters';

type Props = {
  objectAttributes?: Record<string, unknown> | null;
  category?: Category | null;
  maxAttributes?: number;
};

/**
 * Attributs objet sur carte — même présentation que AnnouncementCardFilterAttributes (dashboard user).
 */
const FoundObjectCardAttributes: React.FC<Props> = ({
  objectAttributes,
  category,
  maxAttributes,
}) => {
  const [expanded, setExpanded] = useState(false);
  const attrs = objectAttributes ?? {};
  const defs = categoryAttributesRecord(category);
  const keys = orderedCardAttributeKeys(attrs, category);
  const hasSecretDetail = collectSecretAttributeEntries(attrs, category).length > 0;

  if (keys.length === 0 && !hasSecretDetail) return null;

  const visibleKeys = maxAttributes != null ? keys.slice(0, maxAttributes) : keys;

  const stopCardClickPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  if (keys.length === 0 && hasSecretDetail) {
    return (
      <Box sx={{ mb: 1 }} onClick={stopCardClickPropagation}>
        <FoundObjectSecretDetailButton objectAttributes={attrs} category={category} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1 }} onClick={stopCardClickPropagation}>
      <Box
        component="button"
        type="button"
        aria-expanded={expanded}
        onClick={(event) => {
          event.stopPropagation();
          setExpanded((open) => !open);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
          border: 'none',
          bgcolor: 'transparent',
          cursor: 'pointer',
          p: 0,
          m: 0,
          textAlign: 'left',
          color: 'inherit',
          '&:hover .found-object-details-chevron': {
            color: DASHBOARD_BRAND_PRIMARY,
          },
        }}
      >
        <ChevronRightIcon
          className="found-object-details-chevron"
          sx={{
            fontSize: 22,
            color: 'rgba(13, 40, 64, 0.45)',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.22s ease, color 0.2s ease',
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: 'rgba(13, 40, 64, 0.65)',
            fontSize: '0.8125rem',
          }}
        >
          Détails
        </Typography>
      </Box>

      <Collapse in={expanded} timeout={220}>
        <Box
          sx={{
            pt: 1,
            mt: 0.5,
            borderTop: '1px solid rgba(13, 40, 64, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.85,
          }}
        >
          {visibleKeys.map((key) => {
            const def = defs[key];
            const label = resolveCategoryAttributeDisplayLabel(key, defs, attrs);
            const rawValue = resolveObjectAttributeValue(attrs, key, def) ?? attrs[key];
            const { text, colorHex } = formatAttributeValueForCard(rawValue, def);

            return (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1.25,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(13, 40, 64, 0.6)',
                    fontSize: '0.78rem',
                    fontWeight: 500,
                    flex: '1 1 50%',
                    pr: 0.5,
                  }}
                >
                  {label}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    justifyContent: 'flex-end',
                    flex: '0 1 50%',
                  }}
                >
                  {colorHex ? (
                    <Box
                      aria-hidden
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '4px',
                        border: '1px solid rgba(13, 40, 64, 0.15)',
                        bgcolor: colorHex,
                        flexShrink: 0,
                      }}
                    />
                  ) : null}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#212B36',
                      fontSize: '0.78rem',
                      textAlign: 'right',
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>

      {hasSecretDetail ? (
        <Box sx={{ mt: 1 }}>
          <FoundObjectSecretDetailButton objectAttributes={attrs} category={category} />
        </Box>
      ) : null}
    </Box>
  );
};

export default FoundObjectCardAttributes;
